"use client";

import {
  useIsBlockedFromCommunity,
  useIsBlockedFromManagingProfiles,
  useIsBlockedFromPurchasing,
  useIsKidAccount,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { ReactNode } from "react";

import { useAccessRestrictedGuard } from "../../hooks/useAccessRestrictedGuard";
import type { Block } from "../../lib/accessRestrictions";

type AccessRestrictedGateProps = {
  children: ReactNode;
  blocks?: Block[];
};

export const AccessRestrictedGate = ({
  children,
  blocks = [],
}: AccessRestrictedGateProps) => {
  const { isKidAccount } = useIsKidAccount();
  const isBlockedFromCommunity = useIsBlockedFromCommunity();
  const isBlockedFromPurchasing = useIsBlockedFromPurchasing();
  const isBlockedFromManagingProfiles = useIsBlockedFromManagingProfiles();

  const { status: sessionStatus } = useSession();
  const {
    query: { data: meData },
  } = useRequestMe();

  /* While we don't yet know who the user is, hide gated content so
   policy-blocked users never see it. Only matters on routes that opt into
   a block — unrelated pages keep rendering normally. */
  const isCheckLoading =
    sessionStatus === "loading" ||
    (sessionStatus === "authenticated" && !meData);

  const restricted =
    !isCheckLoading &&
    ((blocks.includes("kid") && isKidAccount) ||
      (blocks.includes("community") && isBlockedFromCommunity) ||
      (blocks.includes("purchasing") && isBlockedFromPurchasing) ||
      (blocks.includes("managingProfiles") && isBlockedFromManagingProfiles));

  const { isRestricted } = useAccessRestrictedGuard({
    restricted: !!restricted,
  });

  if (blocks.length > 0 && isCheckLoading) return null;
  if (isRestricted) return null;
  return <>{children}</>;
};
